import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../api";
import { PageHeader, dt } from "../../components/common";
import { toast } from "sonner";
import { Copy, GitMerge, Check, ExternalLink, RefreshCw } from "lucide-react";
import { useAutoRefresh } from "../../lib/autoRefresh";

export default function Duplicates() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/crm/duplicates");
      setRows(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(load);

  const merge = async (sourceId, targetId, sourceNome, targetNome) => {
    if (!window.confirm(`Fundir "${sourceNome}" na conta "${targetNome}"?\n\nTodos os contactos, negocios, atividades, reunioes e chats de "${sourceNome}" passam para "${targetNome}" e a conta duplicada e eliminada. Esta accao nao pode ser revertida.`)) return;
    setBusy(sourceId);
    try {
      await api.post(`/crm/accounts/${sourceId}/merge`, { target_id: targetId });
      toast.success(`Contas fundidas em "${targetNome}".`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const dismiss = async (id, nome) => {
    if (!window.confirm(`Marcar "${nome}" como NAO duplicado? Deixa de aparecer nesta lista.`)) return;
    setBusy(id);
    try {
      await api.post(`/crm/accounts/${id}/dismiss-duplicate`);
      toast.success("Marcada como nao duplicada.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const runScan = async () => {
    setBusy("scan");
    try {
      const { data } = await api.post("/crm/duplicates/scan");
      toast.success(`Verificacao concluida: ${data.flagged} sinalizada(s) em ${data.scanned} contas.`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div>
      <PageHeader overline="Higiene de dados (CRM)" title="Rever possiveis duplicados"
        action={<button onClick={runScan} disabled={busy === "scan"} data-testid="dup-scan-btn"
          className="inline-flex items-center gap-1.5 text-sm text-white bg-blue hover:opacity-90 rounded-lg px-3 py-2 disabled:opacity-50">
          <RefreshCw size={15} className={busy === "scan" ? "animate-spin" : ""} /> {busy === "scan" ? "A verificar..." : "Verificar agora"}
        </button>} />
      <p className="text-xs text-muted2 mb-1 max-w-3xl">Uma verificacao automatica corre diariamente. Pode tambem corre-la agora.</p>
      <p className="text-xs text-muted2 mb-4 max-w-3xl">
        Estes leads foram criados com um nome de empresa semelhante a uma conta existente mas sem elementos
        suficientes em comum para associacao automatica. Reveja e escolha se os funde numa conta existente
        (um clique) ou se os marca como nao duplicados.
      </p>

      {loading && <div className="text-muted2 text-sm">A carregar...</div>}
      {!loading && rows.length === 0 && (
        <div className="border border-tint rounded-xl p-8 text-center bg-white" data-testid="duplicates-empty">
          <Check className="mx-auto text-green-500 mb-2" />
          <p className="text-navy font-semibold">Sem duplicados por rever</p>
          <p className="text-muted2 text-sm mt-1">Todas as leads estao limpas.</p>
        </div>
      )}

      <div className="space-y-4">
        {rows.map((r) => (
          <div key={r.id} className="border border-tint rounded-xl p-4 bg-white" data-testid={`dup-row-${r.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <Copy size={15} className="text-amber-500" />
                  <Link to={`/app/crm/contas/${r.id}`} className="font-semibold text-navy hover:underline" data-testid={`dup-open-${r.id}`}>{r.nome}</Link>
                  <span className="text-[10px] uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-300 rounded px-1.5 py-0.5">por rever</span>
                </div>
                <div className="text-[11px] text-muted2 mt-0.5">
                  {r.nif ? `NIF ${r.nif} - ` : ""}{r.origem || "-"} - {dt(r.criado_em)} - {r.n_contactos} contacto(s), {r.n_deals} negocio(s)
                </div>
              </div>
              <button onClick={() => dismiss(r.id, r.nome)} disabled={busy === r.id} data-testid={`dup-dismiss-${r.id}`}
                className="text-xs text-slate-600 border border-tint hover:bg-tint rounded-lg px-3 py-1.5 disabled:opacity-40">
                Nao e duplicado
              </button>
            </div>

            <div className="mt-3 border-t border-tint pt-3">
              <p className="text-[11px] font-semibold text-muted2 mb-2">Associar (fundir) a uma destas contas:</p>
              <div className="space-y-2">
                {(r.candidatos || []).length === 0 && <p className="text-xs text-muted2">Sem candidatos.</p>}
                {(r.candidatos || []).map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-3 bg-tint/40 rounded-lg px-3 py-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Link to={`/app/crm/contas/${c.id}`} target="_blank" className="text-sm font-medium text-navy hover:underline truncate">{c.nome}</Link>
                        <ExternalLink size={11} className="text-muted2" />
                      </div>
                      <div className="text-[11px] text-muted2">
                        {c.nif ? `NIF ${c.nif} - ` : ""}{c.tipo || "lead"} - semelhanca {Math.round((c.score || 0) * 100)}% - {c.corroboracao || 0} elemento(s) em comum
                      </div>
                    </div>
                    <button onClick={() => merge(r.id, c.id, r.nome, c.nome)} disabled={busy === r.id} data-testid={`dup-merge-${r.id}-${c.id}`}
                      className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-blue hover:opacity-90 rounded-lg px-3 py-1.5 disabled:opacity-40">
                      <GitMerge size={13} /> Fundir aqui
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
